import { useCallback } from 'react';

import { setClassName } from '@change-corgi/core/error';
import { GQLResponsePerimeterXChallengeError } from '@change-corgi/core/gql';
import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { usePerimeterXTracking } from '#src/app/shared/hooks/payments';
import { usePerimeterXCaptcha } from '#src/app/shared/hooks/perimeterX';

// Identifies this flow in the px_captcha_* analytics, matching the convention used by the other
// protected flows (corgi_promote, corgi_sign, corgi_ambassadors_contract, ...).
const FORM = 'homepage_social_post';

type WithPerimeterXCaptcha = <T>(request: () => Promise<T>, properties?: TrackingProperties) => Promise<T>;

/**
 * Thrown when the retry after a solved captcha is challenged again, so callers can tell it apart
 * from an unsolved captcha (which rethrows the original GQLResponsePerimeterXChallengeError).
 * The retry's challenge is kept as `cause`.
 */
export class PerimeterXRechallengeError extends Error {
	constructor(cause: GQLResponsePerimeterXChallengeError) {
		super('PerimeterX challenged the retry after a solved captcha', { cause });
	}
}
setClassName(PerimeterXRechallengeError, 'PerimeterXRechallengeError');

/**
 * Both social post routes are in the router's px_enforced_routes, so PerimeterX can answer either
 * request with a challenge instead of data. Runs `request`, and on a challenge opens the captcha and
 * retries once if it's solved. An unsolved (or abandoned) challenge rethrows the original error so
 * callers fall through to their normal error handling, and a challenged retry throws a
 * PerimeterXRechallengeError. `properties` (e.g. the link) is added to the px_captcha_* events.
 */
export function useWithPerimeterXCaptcha(): WithPerimeterXCaptcha {
	const { trackShow, trackSuccess, trackFailure } = usePerimeterXTracking(FORM);
	const triggerCaptcha = usePerimeterXCaptcha();

	return useCallback(
		async <T>(request: () => Promise<T>, properties?: TrackingProperties): Promise<T> => {
			try {
				return await request();
			} catch (error) {
				if (!(error instanceof GQLResponsePerimeterXChallengeError)) throw error;

				trackShow(error.operationName, properties);
				const solved = await triggerCaptcha({ pxResponse: error.pxChallenge });
				if (!solved) {
					trackFailure(error.pxChallenge, error.operationName, properties);
					throw error;
				}
				trackSuccess(error.pxChallenge, error.operationName, properties);

				// Only one retry, so a challenge that keeps coming back can't trap the user in a loop.
				try {
					return await request();
				} catch (retryError) {
					if (retryError instanceof GQLResponsePerimeterXChallengeError) {
						throw new PerimeterXRechallengeError(retryError);
					}
					throw retryError;
				}
			}
		},
		[triggerCaptcha, trackShow, trackSuccess, trackFailure],
	);
}
